import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  Stack,
  Chip,
  Paper,
  Tab,
  Tabs,
  Divider,
  alpha
} from '@mui/material';
import {
  Analytics as AnalyticsIcon,
  ArrowBack as ArrowBackIcon,
  Code as CodeIcon,
  Speed as SpeedIcon,
  Security as SecurityIcon,
  Hub as IntegrationIcon,
  Description as DocsIcon,
  PlayArrow as PlayIcon
} from '@mui/icons-material';

import civiqLogo from '../img/civiq.jpg';

const API = ({ onNavigate }) => {
  const [selectedTab, setSelectedTab] = useState(0);

  const endpoints = [
    {
      method: 'GET',
      path: '/api/constituencies',
      description: 'Get list of all constituencies',
      response: {
        "success": true,
        "data": [
          {
            "code": "S03-001",
            "name": "Adilabad",
            "state": "Telangana",
            "fullName": "Adilabad (Telangana)"
          }
        ]
      }
    },
    {
      method: 'GET',
      path: '/api/constituency/{code}',
      description: 'Get detailed constituency data',
      response: {
        "success": true,
        "data": {
          "constituency": "001-Adilabad-GEN",
          "state": "S03-Telangana",
          "electors": {
            "total": {
              "total": 1456789,
              "men": 734567,
              "women": 722222
            }
          },
          "result": {
            "winner": {
              "party": "BJP",
              "candidate": "Soyam Bapu Rao",
              "votes": 567890
            }
          }
        }
      }
    },
    {
      method: 'GET',
      path: '/api/constituencies/search',
      description: 'Search constituencies by name',
      response: {
        "success": true,
        "data": [
          {
            "code": "S03-001",
            "name": "Adilabad",
            "state": "Telangana",
            "match_score": 1.0
          }
        ]
      }
    },
    {
      method: 'GET',
      path: '/api/news/{constituency}',
      description: 'Get news articles for constituency',
      response: {
        "success": true,
        "data": [
          {
            "title": "Development works progress in Adilabad",
            "description": "Infrastructure projects showing significant progress...",
            "source": "The Hindu",
            "sentiment": "Positive",
            "publishedAt": "2025-08-12T10:30:00Z"
          }
        ]
      }
    }
  ];

  const features = [
    {
      icon: <SpeedIcon />,
      title: 'High Performance',
      description: 'Sub-second response times with optimized queries and caching',
      color: '#1976d2'
    },
    {
      icon: <SecurityIcon />,
      title: 'Secure Access',
      description: 'API key authentication with rate limiting and usage monitoring',
      color: '#d32f2f'
    },
    {
      icon: <IntegrationIcon />,
      title: 'Easy Integration',
      description: 'RESTful API with comprehensive documentation and SDKs',
      color: '#388e3c'
    },
    {
      icon: <DocsIcon />,
      title: 'Comprehensive Docs',
      description: 'Detailed documentation with examples and interactive testing',
      color: '#f57c00'
    }
  ];

  const plans = [
    {
      name: 'Developer',
      requests: '1,000',
      price: 'Free',
      features: ['Basic endpoints', 'Community support', 'Rate limited']
    },
    {
      name: 'Professional',
      requests: '10,000',
      price: '₹2,999/month',
      features: ['All endpoints', 'Email support', 'Higher rate limits']
    },
    {
      name: 'Enterprise',
      requests: 'Unlimited',
      price: 'Custom',
      features: ['Custom endpoints', 'Dedicated support', 'SLA guarantees']
    }
  ];

  return (
    <Box sx={{ backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      {/* Header */}
      <AppBar position="static" sx={{ background: 'linear-gradient(135deg, #1976d2 0%, #7b1fa2 100%)' }}>
        <Toolbar>
          <IconButton 
            edge="start" 
            color="inherit" 
            onClick={() => onNavigate('landing')}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
            <Box
              component="img"
              src={civiqLogo}
              alt="CivIQ Logo"
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1,
                mr: 1,
                border: '1px solid rgba(255,255,255,0.3)'
              }}
            />
            {/* <AnalyticsIcon sx={{ mr: 1 }} /> */}
            <Typography variant="h6" fontWeight="bold">
              CivIQ API
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Hero Section */}
      <Box sx={{ py: 8, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
        <Container maxWidth="lg">
          <Box textAlign="center">
            <Typography variant="h2" fontWeight="bold" gutterBottom>
              Powerful API for
            </Typography>
            <Typography variant="h2" fontWeight="bold" gutterBottom sx={{ 
              background: 'linear-gradient(45deg, #ffd700, #ffeb3b)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              Election Data
            </Typography>
            <Typography variant="h6" sx={{ maxWidth: 600, mx: 'auto', mt: 3, mb: 4, opacity: 0.9 }}>
              Access comprehensive Indian election data through our RESTful API. 
              Perfect for developers, researchers, and data scientists.
            </Typography>
            <Stack direction="row" spacing={2} justifyContent="center">
              <Button 
                variant="contained" 
                size="large"
                startIcon={<PlayIcon />}
                sx={{
                  backgroundColor: 'white',
                  color: 'primary.main',
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: alpha('#fff', 0.9)
                  }
                }}
              >
                Try API Now
              </Button>
              <Button 
                variant="outlined" 
                size="large"
                startIcon={<DocsIcon />}
                sx={{
                  borderColor: 'white',
                  color: 'white',
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: alpha('#fff', 0.1)
                  }
                }}
              >
                View Docs
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* Features */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h3" fontWeight="bold" textAlign="center" gutterBottom>
          API Features
        </Typography>
        <Typography variant="h6" textAlign="center" color="text.secondary" sx={{ mb: 6 }}>
          Built for developers who need reliable, fast, and comprehensive election data
        </Typography>
        
        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card sx={{ 
                height: '100%',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 12px 24px ${alpha(feature.color, 0.15)}`
                }
              }}>
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      background: alpha(feature.color, 0.1),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2
                    }}>
                      {React.cloneElement(feature.icon, { 
                        sx: { color: feature.color, fontSize: 24 } 
                      })}
                    </Box>
                  </Box>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    {feature.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* API Documentation */}
      <Box sx={{ py: 8, backgroundColor: 'white' }}>
        <Container maxWidth="lg">
          <Typography variant="h3" fontWeight="bold" textAlign="center" gutterBottom>
            API Endpoints
          </Typography>
          <Typography variant="h6" textAlign="center" color="text.secondary" sx={{ mb: 6 }}>
            Explore our comprehensive API endpoints with live examples
          </Typography>

          <Tabs 
            value={selectedTab} 
            onChange={(e, newValue) => setSelectedTab(newValue)}
            sx={{ mb: 4 }}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Constituencies" />
            <Tab label="Demographics" />
            <Tab label="Elections" />
            <Tab label="News" />
          </Tabs>

          <Grid container spacing={4}>
            {endpoints.map((endpoint, index) => (
              <Grid item xs={12} key={index}>
                <Paper sx={{ p: 3, border: '1px solid #e0e0e0' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Chip 
                      label={endpoint.method}
                      size="small"
                      sx={{ 
                        backgroundColor: '#4caf50',
                        color: 'white',
                        fontWeight: 600,
                        mr: 2
                      }}
                    />
                    <Typography variant="h6" fontWeight="600" component="code" sx={{ fontFamily: 'monospace' }}>
                      {endpoint.path}
                    </Typography>
                  </Box>
                  
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                    {endpoint.description}
                  </Typography>

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                    Example Response:
                  </Typography>
                  <Paper sx={{ 
                    p: 2, 
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #e9ecef',
                    overflow: 'auto'
                  }}>
                    <Typography component="pre" sx={{ 
                      fontFamily: 'monospace',
                      fontSize: '0.875rem',
                      margin: 0,
                      whiteSpace: 'pre-wrap'
                    }}>
                      {JSON.stringify(endpoint.response, null, 2)}
                    </Typography>
                  </Paper>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Pricing */}
      <Box sx={{ py: 8, backgroundColor: '#f8fafc' }}>
        <Container maxWidth="lg">
          <Typography variant="h3" fontWeight="bold" textAlign="center" gutterBottom>
            API Pricing
          </Typography>
          <Typography variant="h6" textAlign="center" color="text.secondary" sx={{ mb: 6 }}>
            Choose the plan that fits your usage needs
          </Typography>

          <Grid container spacing={4} justifyContent="center">
            {plans.map((plan, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card sx={{ 
                  height: '100%',
                  textAlign: 'center',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 24px rgba(0,0,0,0.15)'
                  }
                }}>
                  <CardContent sx={{ p: 4 }}>
                    <Typography variant="h5" fontWeight="bold" gutterBottom>
                      {plan.name}
                    </Typography>
                    <Typography variant="h4" fontWeight="bold" color="primary" gutterBottom>
                      {plan.requests}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      requests per month
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" sx={{ mb: 3 }}>
                      {plan.price}
                    </Typography>
                    
                    <Stack spacing={1} sx={{ mb: 3 }}>
                      {plan.features.map((feature, featureIndex) => (
                        <Typography key={featureIndex} variant="body2" color="text.secondary">
                          • {feature}
                        </Typography>
                      ))}
                    </Stack>
                    
                    <Button 
                      variant={index === 1 ? "contained" : "outlined"}
                      fullWidth
                      onClick={() => onNavigate('login')}
                      sx={{ py: 1.5, fontWeight: 600 }}
                    >
                      {index === 0 ? 'Start Free' : 'Get Started'}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ py: 8, background: 'linear-gradient(135deg, #1976d2 0%, #7b1fa2 100%)', color: 'white' }}>
        <Container maxWidth="md">
          <Box textAlign="center">
            <Typography variant="h3" fontWeight="bold" gutterBottom>
              Ready to Build?
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
              Start building with our API today and access comprehensive election data
            </Typography>
            <Stack direction="row" spacing={2} justifyContent="center">
              <Button 
                variant="contained" 
                size="large"
                startIcon={<CodeIcon />}
                onClick={() => onNavigate('login')}
                sx={{
                  backgroundColor: 'white',
                  color: 'primary.main',
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: alpha('#fff', 0.9)
                  }
                }}
              >
                Get API Key
              </Button>
              <Button 
                variant="outlined" 
                size="large"
                startIcon={<DocsIcon />}
                sx={{
                  borderColor: 'white',
                  color: 'white',
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: alpha('#fff', 0.1)
                  }
                }}
              >
                Read Docs
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>
      {/* Footer */}
            <Box sx={{ py: 6, backgroundColor: '#263238', color: 'white' }}>
              <Container maxWidth="lg">
                <Grid container spacing={4}>
                  <Grid item xs={12} md={6}>
                    <Box display="flex" alignItems="center" mb={2}>
                      <Box 
                        sx={{ 
                          width: 40, 
                          height: 40, 
                          // background: 'linear-gradient(135deg, #1976d2 0%, #7b1fa2 100%)',
                          borderRadius: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 2
                        }}
                      >
                        {/* <AnalyticsIcon sx={{ color: 'white', fontSize: 24 }} /> */}
                        <Box
                                      component="img"
                                      src={civiqLogo}
                                      alt="CivIQ Logo"
                                      sx={{
                                        width: 40,
                                        height: 40,
                                        borderRadius: 1,
                                        mr: 1,
                                        border: '1px solid rgba(255,255,255,0.3)'
                                      }}
                                    />
                      </Box>
                      <Typography variant="h5" fontWeight="bold">
                        CivIQ
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="rgba(255,255,255,0.7)" sx={{ maxWidth: 400 }}>
                      AI-powered political analytics platform for India's elections. Making complex political data accessible and actionable.
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                          Platform
                        </Typography>
                        <Box>
                          <Button 
                            color="inherit" 
                            size="small" 
                            onClick={() => onNavigate('features')}
                            sx={{ justifyContent: 'flex-start', p: 0.5 }}
                          >
                            Features
                          </Button>
                        </Box>
                        <Box>
                          <Button 
                            color="inherit" 
                            size="small" 
                            onClick={() => onNavigate('pricing')}
                            sx={{ justifyContent: 'flex-start', p: 0.5 }}
                          >
                            Pricing
                          </Button>
                        </Box>
                        <Box>
                          <Button 
                            color="inherit" 
                            size="small" 
                            onClick={() => onNavigate('api')}
                            sx={{ justifyContent: 'flex-start', p: 0.5 }}
                          >
                            API
                          </Button>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                          Company
                        </Typography>
                        <Box>
                          <Button 
                            color="inherit" 
                            size="small" 
                            onClick={() => onNavigate('about')}
                            sx={{ justifyContent: 'flex-start', p: 0.5 }}
                          >
                            About
                          </Button>
                        </Box>
                        <Box>
                          <Button 
                            color="inherit" 
                            size="small" 
                            onClick={() => onNavigate('contact')}
                            sx={{ justifyContent: 'flex-start', p: 0.5 }}
                          >
                            Contact
                          </Button>
                        </Box>
                        <Box>
                          <Button 
                            color="inherit" 
                            size="small" 
                            onClick={() => onNavigate('privacy')}
                            sx={{ justifyContent: 'flex-start', p: 0.5 }}
                          >
                            Privacy Policy
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
                
                <Divider sx={{ my: 4, borderColor: 'rgba(255,255,255,0.2)' }} />
                
                <Box textAlign="center">
                  <Typography variant="body2" color="rgba(255,255,255,0.7)">
                    © 2025 CivIQ. All rights reserved.
                  </Typography>
                </Box>
              </Container>
            </Box>
    </Box>
  );
};

export default API;
